import React, { useEffect, useState, useCallback, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Line, Pie, Bar, Radar } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend, ArcElement, Filler, BarElement, RadialLinearScale
} from "chart.js";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import "dayjs/locale/en";
import { DatePicker, Modal } from "antd";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain, faBolt, faChartLine, faUsers,
  faLayerGroup, faDatabase, faMicrochip, faSpinner, faInfoCircle
} from "@fortawesome/free-solid-svg-icons";

import { jwtDecode } from "jwt-decode";
import apiTokenConsumption from "../../services/api/apiTokenConsumption";
import evaApi from "../../services/api/evaApi";
import useAppSession from "../../stores/useAppSession.js";

const { RangePicker } = DatePicker;

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, Title,
  Tooltip, Legend, ArcElement, Filler, BarElement, RadialLinearScale
);

const MONTHS_LOOKBACK = 6;

function normalizeRoles(roles) {
  return (roles || []).map((role) => String(role).toUpperCase());
}

/**
 * ADMIN/ADMIN_IA → API `all` + billing_total (equiv. à query no keyspace inteiro).
 * Demais roles → clientId + billing (apenas aquele cliente).
 */
function getConsumptionScope(roles, clientId) {
  const normalized = normalizeRoles(roles);
  const isAdmin = normalized.includes("ADMIN") || normalized.includes("ADMIN_IA");
  if (isAdmin) {
    return {
      userType: "all",
      scope: "keyspace",
      dataSource: "billing_total",
      reportClientId: clientId,
    };
  }
  if (!clientId) {
    return { userType: null, scope: "missing", dataSource: null, reportClientId: null };
  }
  return {
    userType: clientId,
    scope: "client",
    dataSource: "billing",
    reportClientId: clientId,
  };
}

function pickNumeric(row, keys) {
  for (const key of keys) {
    if (row[key] == null || row[key] === "") continue;
    const n = Number(row[key]);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Mesma regra da tabela mensal / CardGrid legado:
 * total = message_tokens + file_tokens (billing + billing_total).
 */
function consumptionTokensFromRow(row = {}) {
  const messagePart = pickNumeric(row, ["message_tokens", "messageTokens"]) ?? 0;
  const filePart = pickNumeric(row, ["file_tokens", "fileTokens"]) ?? 0;
  const sumParts = messagePart + filePart;
  if (sumParts > 0) {
    return { messagePart, filePart, total: sumParts };
  }
  const apiTotal = pickNumeric(row, ["total_tokens", "totalTokens"]);
  return {
    messagePart,
    filePart,
    total: apiTotal != null ? apiTotal : 0,
  };
}

function resolveDashboardDateRange(period, customRange) {
  if (customRange?.[0] && customRange?.[1]) {
    const start = customRange[0].startOf("day");
    const end = customRange[1].startOf("day");
    return {
      start: start.format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
      dayCount: end.diff(start, "day") + 1,
    };
  }
  const end = dayjs().startOf("day");
  const start = end.subtract(period - 1, "day");
  return {
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
    dayCount: period,
  };
}

function parseConsumptionDateValue(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  const isoDate = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  const parsed = dayjs(isoDate ? isoDate[1] : raw);
  if (!parsed.isValid()) return null;
  return parsed.startOf("day").format("YYYY-MM-DD");
}

/** Data canônica da linha (billing/day/month/year antes de created_date). */
function consumptionDayKey(item, rangeStart, rangeEnd) {
  if (item.day != null && item.month != null) {
    const monthNum = Number(item.month);
    const dayNum = Number(item.day);
    if (Number.isFinite(monthNum) && Number.isFinite(dayNum) && dayNum >= 1) {
      const month = String(item.month).padStart(2, "0");
      const day = String(item.day).padStart(2, "0");
      if (item.year != null && item.year !== "") {
        const fromParts = parseConsumptionDateValue(`${item.year}-${month}-${day}`);
        if (fromParts) return fromParts;
      }
    }
  }

  const dateFields = [
    "billing_date",
    "reference_date",
    "consumption_date",
    "date",
    "day_date",
    "created_date",
  ];
  for (const field of dateFields) {
    const key = parseConsumptionDateValue(item[field]);
    if (key) return key;
  }

  if (item.day == null || item.month == null) return null;

  const monthNum = Number(item.month);
  const dayNum = Number(item.day);
  if (!Number.isFinite(monthNum) || !Number.isFinite(dayNum) || dayNum < 1) {
    return null;
  }

  const month = String(item.month).padStart(2, "0");
  const day = String(item.day).padStart(2, "0");

  const candidates = [
    dayjs(`${rangeStart.slice(0, 4)}-${month}-${day}`),
    dayjs(`${rangeEnd.slice(0, 4)}-${month}-${day}`),
  ];
  const start = dayjs(rangeStart);
  const end = dayjs(rangeEnd);
  const inRange = candidates.find(
    (d) => d.isValid() && !d.isBefore(start, "day") && !d.isAfter(end, "day")
  );
  const picked = inRange || candidates.find((d) => d.isValid());
  if (picked) return picked.format("YYYY-MM-DD");

  const targetMonth = Number(item.month);
  const targetDay = Number(item.day);
  if (!Number.isFinite(targetMonth) || !Number.isFinite(targetDay)) return null;

  let walk = start.clone();
  while (!walk.isAfter(end, "day")) {
    if (walk.month() + 1 === targetMonth && walk.date() === targetDay) {
      return walk.format("YYYY-MM-DD");
    }
    walk = walk.add(1, "day");
  }
  return null;
}

function emptyDayBucket() {
  return {
    tokens: 0,
    msg: 0,
    file: 0,
    ratings: 0,
    pos: 0,
    neg: 0,
    fileSizeKB: 0,
    msgSizeKB: 0,
  };
}

function addItemToBucket(bucket, item, messagePart, filePart, total) {
  return {
    tokens: bucket.tokens + total,
    msg: bucket.msg + messagePart,
    file: bucket.file + filePart,
    ratings: bucket.ratings + (Number(item.ratings_total) || 0),
    pos: bucket.pos + (Number(item.ratings_count_positive) || 0),
    neg: bucket.neg + (Number(item.ratings_count_negative) || 0),
    fileSizeKB: bucket.fileSizeKB + (Number(item.file_size) || 0),
    msgSizeKB: bucket.msgSizeKB + (Number(item.message_size) || 0),
  };
}

/** Agrega billing no período; totais somam todos os registros da API (já filtrados no backend). */
function aggregateConsumptionInRange(consumption, startStr, endStr) {
  const start = dayjs(startStr).startOf("day");
  const end = dayjs(endStr).startOf("day");
  const byDay = new Map();
  const totals = {
    msgTokens: 0,
    fileTokens: 0,
    totalTokens: 0,
    pos: 0,
    neg: 0,
    totalRatings: 0,
    fileSizeKB: 0,
    msgSizeKB: 0,
  };

  (consumption || []).forEach((item) => {
    const key = consumptionDayKey(item, startStr, endStr);
    if (key) {
      const day = dayjs(key);
      if (!day.isValid() || day.isBefore(start, "day") || day.isAfter(end, "day")) {
        return;
      }
    }

    const { messagePart, filePart, total } = consumptionTokensFromRow(item);

    totals.msgTokens += messagePart;
    totals.fileTokens += filePart;
    totals.totalTokens += total;
    totals.pos += Number(item.ratings_count_positive) || 0;
    totals.neg += Number(item.ratings_count_negative) || 0;
    totals.totalRatings += Number(item.ratings_total) || 0;
    totals.fileSizeKB += Number(item.file_size) || 0;
    totals.msgSizeKB += Number(item.message_size) || 0;

    if (!key) return;
    byDay.set(key, addItemToBucket(byDay.get(key) || emptyDayBucket(), item, messagePart, filePart, total));
  });

  const labels = [];
  const tokenTimeline = [];
  const ratingTimeline = [];

  let cursor = start;
  while (!cursor.isAfter(end, "day")) {
    const key = cursor.format("YYYY-MM-DD");
    const row = byDay.get(key) || emptyDayBucket();
    labels.push(cursor.format("DD/MM/YY"));
    tokenTimeline.push(row.tokens);
    ratingTimeline.push(row.ratings);
    cursor = cursor.add(1, "day");
  }

  return { ...totals, labels, tokenTimeline, ratingTimeline };
}

/** Ajusta a série diária quando o total oficial (ex.: mensal) difere da soma dos dias. */
function reconcileTokenTimelineWithTotal(tokenTimeline, targetTotal) {
  const safeTarget = Number(targetTotal) || 0;
  const sum = tokenTimeline.reduce((acc, value) => acc + value, 0);
  if (sum === safeTarget) return tokenTimeline;
  if (!tokenTimeline.length) return tokenTimeline;
  if (sum === 0) {
    const next = tokenTimeline.slice();
    next[next.length - 1] = safeTarget;
    return next;
  }
  const next = tokenTimeline.map((value) => Math.round((value / sum) * safeTarget));
  const drift = safeTarget - next.reduce((acc, value) => acc + value, 0);
  if (drift !== 0) {
    next[next.length - 1] += drift;
  }
  return next;
}

function isCurrentCalendarMonthToToday(startStr, endStr) {
  const start = dayjs(startStr).startOf("day");
  const end = dayjs(endStr).startOf("day");
  const today = dayjs().startOf("day");
  if (!start.isSame(end, "month") || !start.isSame(end, "year")) return false;
  if (!end.isSame(today, "day")) return false;
  return start.isSame(start.startOf("month"), "day");
}

function iterateMonthSegmentsInRange(startStr, endStr) {
  const rangeStart = dayjs(startStr).startOf("day");
  const rangeEnd = dayjs(endStr).startOf("day");
  const segments = [];
  let cursor = rangeStart.startOf("month");

  while (!cursor.isAfter(rangeEnd, "month")) {
    const monthStart = cursor.startOf("month");
    const monthEnd = cursor.endOf("month");
    const segStart = monthStart.isBefore(rangeStart, "day") ? rangeStart : monthStart;
    const segEnd = monthEnd.isAfter(rangeEnd, "day") ? rangeEnd : monthEnd;
    const fullMonth =
      segStart.isSame(monthStart, "day") && segEnd.isSame(monthEnd, "day");
    const currentMonthPartial =
      isCurrentCalendarMonthToToday(
        segStart.format("YYYY-MM-DD"),
        segEnd.format("YYYY-MM-DD")
      );

    segments.push({
      key: `${cursor.year()}-${cursor.month() + 1}`,
      fullMonth,
      useMonthlyTotals: fullMonth || currentMonthPartial,
      segStart: segStart.format("YYYY-MM-DD"),
      segEnd: segEnd.format("YYYY-MM-DD"),
    });
    cursor = cursor.add(1, "month");
  }
  return segments;
}

function sumDetailConsumptionInSegment(consumption, segStart, segEnd) {
  const start = dayjs(segStart).startOf("day");
  const end = dayjs(segEnd).startOf("day");
  const totals = {
    msgTokens: 0,
    fileTokens: 0,
    totalTokens: 0,
    msgSizeKB: 0,
    fileSizeKB: 0,
  };

  (consumption || []).forEach((item) => {
    const key = consumptionDayKey(item, segStart, segEnd);
    if (!key) return;
    const day = dayjs(key);
    if (!day.isValid() || day.isBefore(start, "day") || day.isAfter(end, "day")) {
      return;
    }
    const { messagePart, filePart, total } = consumptionTokensFromRow(item);
    totals.msgTokens += messagePart;
    totals.fileTokens += filePart;
    totals.totalTokens += total;
    totals.msgSizeKB += Number(item.message_size) || 0;
    totals.fileSizeKB += Number(item.file_size) || 0;
  });

  return totals;
}

/** Tokens/KB oficiais: meses fechados + mês corrente (1º→hoje) via tabela mensal; trechos parciais via detalhe. */
function buildAuthoritativeTokenTotals(consumption, dateRange, monthlyTableRows) {
  const byMonth = new Map(monthlyTableRows.map((row) => [row.key, row]));
  const segments = iterateMonthSegmentsInRange(dateRange.start, dateRange.end);
  const totals = {
    msgTokens: 0,
    fileTokens: 0,
    totalTokens: 0,
    msgSizeKB: 0,
    fileSizeKB: 0,
  };

  segments.forEach((seg) => {
    if (seg.useMonthlyTotals) {
      const row = byMonth.get(seg.key);
      if (row) {
        totals.msgTokens += row.messageTokens;
        totals.fileTokens += row.fileTokens;
        totals.totalTokens += row.totalTokens;
        totals.msgSizeKB += row.messageSize;
        totals.fileSizeKB += row.fileSize;
        return;
      }
    }
    const part = sumDetailConsumptionInSegment(consumption, seg.segStart, seg.segEnd);
    totals.msgTokens += part.msgTokens;
    totals.fileTokens += part.fileTokens;
    totals.totalTokens += part.totalTokens;
    totals.msgSizeKB += part.msgSizeKB;
    totals.fileSizeKB += part.fileSizeKB;
  });

  return totals;
}

function applyAuthoritativeTokenTotals(periodStats, tokenTotals) {
  const totalTokens = tokenTotals.totalTokens;
  return {
    ...periodStats,
    ...tokenTotals,
    dataKb: tokenTotals.msgSizeKB + tokenTotals.fileSizeKB,
    tokenTimeline: reconcileTokenTimelineWithTotal(periodStats.tokenTimeline, totalTokens),
  };
}

function clampScore(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

/** Radar Densidade: média diária de KB; 1024 KB/dia (1 MB/dia) = índice 100. */
const RADAR_DENSITY_KB_PER_DAY_FULL = 1024;

function computeRadarScores(stats, dateRange, messagesLength) {
  const periodDataKb = stats.msgSizeKB + stats.fileSizeKB;

  const precision =
    messagesLength > 0
      ? clampScore(((messagesLength - stats.neg) / messagesLength) * 100)
      : 0;

  const velocity = 100;

  const rag = 100;

  const avgKbPerDay = periodDataKb / Math.max(dateRange.dayCount, 1);
  const density = clampScore((avgKbPerDay / RADAR_DENSITY_KB_PER_DAY_FULL) * 100);

  const retention = 100;

  return { precision, velocity, rag, density, retention };
}

function mergeConsumptionMonthRow(existing, row) {
  const { messagePart, filePart, total } = consumptionTokensFromRow(row);

  if (!existing) {
    return {
      message_tokens: messagePart,
      file_tokens: filePart,
      total_tokens: total,
      message_size: row.message_size || 0,
      file_size: row.file_size || 0,
    };
  }

  return {
    message_tokens: existing.message_tokens + messagePart,
    file_tokens: existing.file_tokens + filePart,
    total_tokens: existing.total_tokens + total,
    message_size: existing.message_size + (row.message_size || 0),
    file_size: existing.file_size + (row.file_size || 0),
  };
}

function getLastSixMonthsRange() {
  const end = dayjs().endOf("month");
  const start = dayjs().subtract(MONTHS_LOOKBACK - 1, "month").startOf("month");
  return {
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
  };
}

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 40px 24px;
  background-color: #f2f2f2;
  min-height: 100vh;
  box-sizing: border-box;
`;

const MainCard = styled.div`
  width: 100%;
  max-width: 1100px;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 32px;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const TitlePage = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const DataScopeNote = styled.p`
  font-size: 12px;
  color: #86868b;
  margin: -16px 0 24px 0;
  line-height: 1.45;
`;

const DateSelector = styled.div`
  display: flex;
  background: #f5f5f7;
  border-radius: 6px;
  padding: 4px;
  align-items: center;
`;

const DateButton = styled.button`
  border: none;
  background: ${props => props.active ? '#FF8F40' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#86868b'};
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: ${props => props.active ? '#FF8F40' : '#e5e5e7'}; }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const MetricCard = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 20px;
  .label { 
    font-size: 11px; 
    font-weight: 700; 
    color: #86868b; 
    text-transform: uppercase; 
    display: flex; 
    gap: 8px; 
    align-items: center;
    margin-bottom: 8px;
  }
  .value { font-size: 24px; font-weight: 700; color: #1d1d1f; }
  .footer { font-size: 11px; margin-top: 8px; color: #FF8F40; font-weight: 600; }
`;

const HealthBreakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`;

const HealthChip = styled.span`
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(props) => props.$color || "#86868b"};
  background: ${(props) => props.$bg || "#f5f5f7"};
  padding: 4px 8px;
  border-radius: 4px;
`;

const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  position: relative;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 24px;
  grid-column: span ${props => props.span || 1};
  min-height: ${props => props.height || '350px'};
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    grid-column: span 4;
  }

  .chart-title { 
    font-size: 14px; 
    font-weight: 700; 
    color: #333; 
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const ChartWrapper = styled.div`
  flex: 1;
  width: 100%;
  min-height: 0;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px;
  color: #666;
  font-size: 15px;
  gap: 16px;
`;

const InfoButton = styled.button`
  background: transparent;
  border: none;
  color: #c1c1c1;
  cursor: pointer;
  padding: 4px;
  position: absolute;
  top: 16px;
  right: 16px;
  transition: color 0.2s;
  z-index: 10;
  &:hover {
    color: #FF8F40;
  }
`;

const MonthlySection = styled.section`
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid #eaeaea;
`;

const SectionHeading = styled.div`
  margin-bottom: 16px;

  h2 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 6px 0;
  }

  p {
    font-size: 12px;
    color: #86868b;
    margin: 0;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #eaeaea;
  border-radius: 6px;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: #fff;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #eaeaea;
  color: #333;
  font-weight: 600;
  white-space: nowrap;

  &.numeric {
    text-align: right;
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #eaeaea;
  color: #666;
  vertical-align: middle;

  &.numeric {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  &.total-col {
    color: #1d1d1f;
    font-weight: 600;
  }
`;

const TotalRow = styled.tr`
  background: #f5f5f7;

  ${Td} {
    color: #1d1d1f;
    font-weight: 600;
    border-bottom: none;
  }
`;

const TablePlaceholder = styled.div`
  padding: 24px;
  text-align: center;
  color: #86868b;
  font-size: 14px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  h3 {
    font-size: 18px;
    color: #1d1d1f;
    margin: 0;
  }
  p {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
    margin: 0;
  }
  ul {
    padding-left: 20px;
    margin: 0;
    li {
      margin-bottom: 8px;
      font-size: 14px;
      color: #666;
      strong {
        color: #333;
      }
    }
  }
`;

const Dashboard = ({ onLogout }) => {
  const intl = useIntl();
  const { clientId, roles, locale, token } = useAppSession();
  const [period, setPeriod] = useState(7);
  const [customRange, setCustomRange] = useState(null);
  const [data, setData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const [messagesLength, setMessagesLength] = useState(0);
  const [messagesByChannel, setMessagesByChannel] = useState({ Web: 0, Widget: 0, Telegram: 0, Whatsapp: 0 });
  const [messagesPerDayByChannel, setMessagesPerDayByChannel] = useState({});
  const [activeModal, setActiveModal] = useState(null);

  const consumptionScope = useMemo(
    () => getConsumptionScope(roles, clientId),
    [roles, clientId]
  );

  const workspaceName = useMemo(() => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded?.user_access?.environment?.workspace?.name || null;
    } catch {
      return null;
    }
  }, [token]);

  const dateRange = useMemo(
    () => resolveDashboardDateRange(period, customRange),
    [period, customRange]
  );

  const fetchData = useCallback(async () => {
    const { userType } = consumptionScope;
    if (!userType) return;
    const { start, end } = dateRange;

    try {
      const response = await apiTokenConsumption.get_tokens(userType, start, end);
      if (response.status === 401) onLogout();
      setData(response.data);

      const resMsg = await evaApi.getMessages(userType, start, end);
      const messages = resMsg.data.messages || [];
      setMessagesLength(messages.length);

      const channelCounts = messages.reduce((acc, m) => {
        const u = m.username.toUpperCase();
        const chan = u.includes("WIDGET") ? "Widget" : u.includes("TELEGRAM") ? "Telegram" : u.includes("WHATSAPP") ? "Whatsapp" : "Web";
        acc[chan] = (acc[chan] || 0) + 1;
        return acc;
      }, { Web: 0, Widget: 0, Telegram: 0, Whatsapp: 0 });
      setMessagesByChannel(channelCounts);

      const perDay = messages.reduce((acc, m) => {
        const date = m.data.split(" ")[0];
        const u = m.username.toUpperCase();
        const chan = u.includes("WIDGET") ? "Widget" : u.includes("TELEGRAM") ? "Telegram" : u.includes("WHATSAPP") ? "Whatsapp" : "Web";
        if (!acc[date]) acc[date] = { Web: 0, Widget: 0, Telegram: 0, Whatsapp: 0 };
        acc[date][chan] += 1;
        return acc;
      }, {});
      setMessagesPerDayByChannel(perDay);
    } catch (e) { console.error(e); }
  }, [consumptionScope, dateRange, onLogout]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchMonthlyData = useCallback(async () => {
    const { userType } = consumptionScope;
    if (!userType) {
      setMonthlyData(null);
      setMonthlyLoading(false);
      return;
    }

    const { start, end } = getLastSixMonthsRange();
    setMonthlyLoading(true);
    try {
      const response = await apiTokenConsumption.get_consumption_by_month(userType, start, end);
      if (response.status === 401) {
        onLogout();
        return;
      }
      setMonthlyData(response.data);
    } catch (e) {
      console.error(e);
      setMonthlyData({ consumption: [] });
    } finally {
      setMonthlyLoading(false);
    }
  }, [consumptionScope, onLogout]);

  useEffect(() => { fetchMonthlyData(); }, [fetchMonthlyData]);

  const monthlyTableRows = useMemo(() => {
    const dayjsLocale = locale === "en" ? "en" : "pt-br";
    const apiByMonth = new Map();
    (monthlyData?.consumption || []).forEach((row) => {
      const key = `${row.year}-${row.month}`;
      apiByMonth.set(key, mergeConsumptionMonthRow(apiByMonth.get(key), row));
    });

    const rows = [];
    for (let i = MONTHS_LOOKBACK - 1; i >= 0; i -= 1) {
      const ref = dayjs().subtract(i, "month").locale(dayjsLocale);
      const year = ref.year();
      const month = ref.month() + 1;
      const apiRow = apiByMonth.get(`${year}-${month}`) || {};
      const { messagePart: messageTokens, filePart: fileTokens, total: totalTokens } =
        consumptionTokensFromRow(apiRow);
      const messageSize = apiRow.message_size || 0;
      const fileSize = apiRow.file_size || 0;
      const alignedTotal =
        messageTokens + fileTokens > 0 ? messageTokens + fileTokens : totalTokens;

      rows.push({
        key: `${year}-${month}`,
        label: ref.format("MMM/YYYY"),
        messageTokens,
        fileTokens,
        totalTokens: alignedTotal,
        messageSize,
        fileSize,
        dataKb: messageSize + fileSize,
      });
    }

    return rows;
  }, [monthlyData, locale]);

  const monthlyTotals = useMemo(() => {
    return monthlyTableRows.reduce(
      (acc, row) => {
        acc.messageTokens += row.messageTokens;
        acc.fileTokens += row.fileTokens;
        acc.totalTokens += row.totalTokens;
        acc.dataKb += row.dataKb;
        return acc;
      },
      { messageTokens: 0, fileTokens: 0, totalTokens: 0, dataKb: 0 }
    );
  }, [monthlyTableRows]);

  const dataScopeLabel = useMemo(() => {
    if (consumptionScope.scope === "missing") {
      return intl.formatMessage({ id: "dashboard.monthlyTable.scopeMissing" });
    }

    const tenant = workspaceName || intl.formatMessage({ id: "dashboard.monthlyTable.workspaceUnknown" });
    const reportClient = consumptionScope.reportClientId || "—";

    if (consumptionScope.scope === "keyspace") {
      return intl.formatMessage(
        { id: "dashboard.monthlyTable.scopeKeyspace" },
        { tenant, reportClient, dataSource: consumptionScope.dataSource }
      );
    }

    return intl.formatMessage(
      { id: "dashboard.monthlyTable.scopeClient" },
      { tenant, reportClient, dataSource: consumptionScope.dataSource }
    );
  }, [consumptionScope, workspaceName, intl]);

  const formatNumber = (value) => value.toLocaleString(locale === "en" ? "en-US" : "pt-BR");

  const stats = useMemo(() => {
    if (!data) return null;
    const periodStats = aggregateConsumptionInRange(
      data.consumption,
      dateRange.start,
      dateRange.end
    );
    const tokenTotals = buildAuthoritativeTokenTotals(
      data.consumption,
      dateRange,
      monthlyTableRows
    );
    return applyAuthoritativeTokenTotals(periodStats, tokenTotals);
  }, [data, dateRange, monthlyTableRows]);

  const radarScores = useMemo(() => {
    if (!stats) return null;
    return computeRadarScores(stats, dateRange, messagesLength);
  }, [stats, dateRange, messagesLength]);

  const sortedMessageDays = useMemo(() => {
    return Object.keys(messagesPerDayByChannel).sort(
      (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
    );
  }, [messagesPerDayByChannel]);

  const cardDefinitions = {
    tokens: {
      title: "Cognição (Tokens)",
      content: (
        <>
          <p>Esta métrica representa a soma total de "tokens" processados pela inteligência artificial no período selecionado. Um token é a unidade básica de texto que a IA lê e gera (aproximadamente 4 caracteres).</p>
          <p>Este número engloba tanto os tokens das perguntas dos usuários quanto as respostas geradas e os arquivos lidos para contexto.</p>
          <p>Administradores (ADMIN/ADMIN_IA) veem o keyspace inteiro (<strong>billing_total</strong>); demais perfis veem apenas o <strong>client_id</strong> da sessão (<strong>billing</strong>).</p>
        </>
      )
    },
    efficiency: {
      title: "Eficiência de Dados",
      content: (
        <>
          <p>A eficiência mede a quantidade média de dados (em Kilobytes) trafegados por cada interação com o sistema.</p>
          <p>Um valor otimizado indica que o sistema está conseguindo entregar respostas ricas sem consumir recursos excessivos de banda ou processamento desnecessário.</p>
        </>
      )
    },
    volume: {
      title: "Volume de Mensagens",
      content: (
        <>
          <p>Contagem absoluta de todas as interações e mensagens recebidas pelo sistema em todos os canais conectados (WhatsApp, Web, Telegram, etc.) no intervalo de tempo escolhido.</p>
        </>
      )
    },
    health: {
      title: "Saúde da Operação",
      content: (
        <>
          <p>O Score de Satisfação é calculado inversamente à taxa de feedback negativo (Churn de interação). Ele reflete a porcentagem de interações que não receberam avaliações negativas explícitas.</p>
          <p>Um score alto indica que as respostas da IA estão atendendo às expectativas dos usuários.</p>
        </>
      )
    },
    timeline: {
      title: "Série Temporal: Consumo vs Feedback",
      content: (
        <>
          <p>Este gráfico cruza duas linhas vitais:</p>
          <ul>
            <li><strong>Tokens (Laranja):</strong> O volume de processamento diário.</li>
            <li><strong>Feedback (Verde):</strong> A quantidade de avaliações recebidas.</li>
          </ul>
          <p>Use este gráfico para identificar se picos de uso correspondem a picos de engajamento ou problemas.</p>
        </>
      )
    },
    channels: {
      title: "Distribuição por Canais",
      content: (
        <>
          <p>Mostra a participação percentual de cada canal de atendimento no volume total de mensagens. Ajuda a identificar onde seu público está mais ativo.</p>
        </>
      )
    },
    stack: {
      title: "Carga Diária por Canal",
      content: (
        <>
          <p>Uma visão detalhada dia a dia que empilha o volume de mensagens de cada canal. Isso permite visualizar não apenas o total diário, mas a composição desse total por origem (ex: quanto do dia foi WhatsApp vs Web).</p>
        </>
      )
    },
    radar: {
      title: "Radar de Performance",
      content: (
        <>
          <p>Índices do período selecionado em escala de 0 a 100:</p>
          <ul>
            <li><strong>Precisão:</strong> (total de mensagens − mensagens negativas) ÷ total de mensagens × 100.</li>
            <li><strong>Velocidade:</strong> fixo em 100% no período.</li>
            <li><strong>RAG:</strong> fixo em 100% — o fluxo sempre consulta a base de conhecimento.</li>
            <li><strong>Densidade:</strong> volume médio diário trafegado em KB, em percentual (1 MB/dia médio = índice 100).</li>
            <li><strong>Retenção:</strong> fixo em 100% (referência de retenção plena no período).</li>
          </ul>
        </>
      )
    },
    summary: {
      title: "Resumo de Eficiência de Dados",
      content: (
        <>
          <p>Métricas consolidadas sobre o uso de recursos:</p>
          <ul>
            <li><strong>Indexado (MB):</strong> Tamanho total da base de conhecimento carregada no sistema.</li>
            <li><strong>Tokens/Doc:</strong> Densidade média de tokens por documento processado.</li>
            <li><strong>Custo Médio KB:</strong> Relação de custo computacional (tokens) para processar cada Kilobyte de informação.</li>
          </ul>
        </>
      )
    }
  };

  const openModal = (key) => setActiveModal(key);
  const closeModal = () => setActiveModal(null);

  if (consumptionScope.scope === "missing") {
    return (
      <PageWrapper>
        <MainCard>
          <TitlePage>{intl.formatMessage({ id: "dashboard.title" })}</TitlePage>
          <TablePlaceholder>
            {intl.formatMessage({ id: "dashboard.monthlyTable.scopeMissing" })}
          </TablePlaceholder>
        </MainCard>
      </PageWrapper>
    );
  }

  if (!data || !stats) {
    return (
      <PageWrapper>
        <MainCard>
          <LoaderWrapper>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color="#FF8F40" />
            <span>{intl.formatMessage({ id: "dashboard.loading" })}</span>
          </LoaderWrapper>
        </MainCard>
      </PageWrapper>
    );
  }

  const totalTokens = stats.totalTokens;
  const periodDataKb = Math.round(stats.msgSizeKB + stats.fileSizeKB);
  const interactionCount = messagesLength * 2;
  const tokensPerMessage =
    interactionCount > 0 ? Math.round(totalTokens / interactionCount) : 0;
  const indexedMB = (periodDataKb / 1024).toFixed(2);
  const tokensPerFileKb =
    stats.fileSizeKB > 0
      ? (stats.fileTokens / stats.fileSizeKB).toFixed(1)
      : "—";
  const tokensPerDataKb =
    periodDataKb > 0 ? (totalTokens / periodDataKb).toFixed(1) : "—";
  const healthPositive = stats.pos;
  const healthNegative = stats.neg;
  const healthNotRated = Math.max(0, messagesLength - stats.totalRatings);
  const satisfactionScore =
    stats.totalRatings > 0
      ? clampScore((stats.pos / stats.totalRatings) * 100)
      : null;

  return (
    <PageWrapper>
      <MainCard>
        <Header>
          <TitlePage>Dashboard</TitlePage>
          <DateSelector>
            {[7, 15, 30].map(d => (
              <DateButton
                key={d}
                active={!customRange && period === d}
                onClick={() => { setCustomRange(null); setPeriod(d); }}
              >
                {d}D
              </DateButton>
            ))}
            <RangePicker
              value={customRange}
              onChange={v => setCustomRange(v)}
              style={{ marginLeft: 12, borderRadius: '6px', height: '32px' }}
            />
          </DateSelector>
        </Header>

        {dataScopeLabel ? <DataScopeNote>{dataScopeLabel}</DataScopeNote> : null}
        <DataScopeNote style={{ marginTop: dataScopeLabel ? -12 : -16 }}>
          {intl.formatMessage(
            { id: "dashboard.periodRange" },
            { start: dateRange.start, end: dateRange.end, days: dateRange.dayCount }
          )}
        </DataScopeNote>

        <MetricsGrid>
          <MetricCard>
            <InfoButton onClick={() => openModal('tokens')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="label"><FontAwesomeIcon icon={faMicrochip} /> Cognição (Tokens)</div>
            <div className="value">{formatNumber(totalTokens)}</div>
            <div className="footer">
              {intl.formatMessage(
                { id: "dashboard.metric.tokensBreakdown" },
                {
                  messages: formatNumber(stats.msgTokens),
                  files: formatNumber(stats.fileTokens),
                }
              )}
            </div>
          </MetricCard>

          <MetricCard>
            <InfoButton onClick={() => openModal('efficiency')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="label"><FontAwesomeIcon icon={faDatabase} /> Eficiência</div>
            <div className="value">{formatNumber(periodDataKb)} KB</div>
            <div className="footer">
              {intl.formatMessage({ id: "dashboard.metric.periodDataKb" })}
            </div>
          </MetricCard>

          <MetricCard>
            <InfoButton onClick={() => openModal('volume')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="label"><FontAwesomeIcon icon={faUsers} /> Volume</div>
            <div className="value">{formatNumber(interactionCount)}</div>
            <div className="footer">
              {intl.formatMessage(
                { id: "dashboard.metric.tokensPerMessage" },
                { avg: formatNumber(tokensPerMessage) }
              )}
            </div>
          </MetricCard>

          <MetricCard>
            <InfoButton onClick={() => openModal('health')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="label"><FontAwesomeIcon icon={faChartLine} /> Saúde</div>
            <div className="value">
              {satisfactionScore != null ? `${satisfactionScore}%` : "—"}
            </div>
            <div className="footer" style={{ color: "#34c759" }}>
              {intl.formatMessage({ id: "dashboard.metric.satisfactionScore" })}
            </div>
            <HealthBreakdown>
              <HealthChip $color="#86868b" $bg="#f5f5f7">
                {intl.formatMessage(
                  { id: "dashboard.health.notRated" },
                  { count: formatNumber(healthNotRated) }
                )}
              </HealthChip>
              <HealthChip $color="#248a3d" $bg="rgba(52, 199, 89, 0.12)">
                {intl.formatMessage(
                  { id: "dashboard.health.positive" },
                  { count: formatNumber(healthPositive) }
                )}
              </HealthChip>
              <HealthChip $color="#c93400" $bg="rgba(255, 77, 79, 0.1)">
                {intl.formatMessage(
                  { id: "dashboard.health.negative" },
                  { count: formatNumber(healthNegative) }
                )}
              </HealthChip>
            </HealthBreakdown>
          </MetricCard>
        </MetricsGrid>

        <BentoGrid>
          <ChartCard span={3}>
            <InfoButton onClick={() => openModal('timeline')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="chart-title">
              <span>Série Temporal: Consumo vs Feedback</span>
              <span style={{ fontSize: "10px", color: "#86868b", fontWeight: 500 }}>
                {intl.formatMessage(
                  { id: "dashboard.chart.timelineTotal" },
                  { total: formatNumber(totalTokens) }
                )}
              </span>
            </div>
            <ChartWrapper>
              <Line
                data={{
                  labels: stats.labels,
                  datasets: [
                    {
                      label: intl.formatMessage({ id: "dashboard.chart.tokens", defaultMessage: "Tokens" }),
                      data: stats.tokenTimeline,
                      borderColor: "#FF8F40",
                      backgroundColor: "rgba(255,143,64,0.12)",
                      fill: true,
                      tension: 0.35,
                      yAxisID: "yTokens",
                    },
                    {
                      label: intl.formatMessage({ id: "dashboard.chart.feedback", defaultMessage: "Feedback" }),
                      data: stats.ratingTimeline,
                      borderColor: "#34c759",
                      backgroundColor: "rgba(52,199,89,0.08)",
                      tension: 0.35,
                      pointRadius: 2,
                      yAxisID: "yFeedback",
                    },
                  ],
                }}
                options={{
                  maintainAspectRatio: false,
                  interaction: { mode: "index", intersect: false },
                  plugins: {
                    legend: {
                      display: true,
                      position: "bottom",
                      labels: { boxWidth: 10, font: { size: 11 } },
                    },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => {
                          const val = formatNumber(ctx.raw ?? 0);
                          return `${ctx.dataset.label}: ${val}`;
                        },
                      },
                    },
                  },
                  scales: {
                    yTokens: {
                      type: "linear",
                      position: "left",
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: "Tokens",
                        color: "#86868b",
                        font: { size: 11 },
                      },
                      ticks: { color: "#86868b" },
                      grid: { color: "#f0f0f0" },
                    },
                    yFeedback: {
                      type: "linear",
                      position: "right",
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: "Feedback",
                        color: "#86868b",
                        font: { size: 11 },
                      },
                      ticks: { color: "#86868b", precision: 0 },
                      grid: { drawOnChartArea: false },
                    },
                  },
                }}
              />
            </ChartWrapper>
          </ChartCard>

          <ChartCard span={1}>
            <InfoButton onClick={() => openModal('channels')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="chart-title">Canais</div>
            <ChartWrapper>
              <Pie
                data={{
                  labels: Object.keys(messagesByChannel),
                  datasets: [{
                    data: Object.values(messagesByChannel),
                    backgroundColor: ['#FF8F40', '#5856d6', '#34c759', '#afafaf'],
                    borderWidth: 0
                  }]
                }}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } }
                  }
                }}
              />
            </ChartWrapper>
          </ChartCard>

          <ChartCard span={2} height="300px">
            <InfoButton onClick={() => openModal('stack')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="chart-title">Carga por Canal (Stack)</div>
            <ChartWrapper>
              <Bar
                data={{
                  labels: sortedMessageDays.map((d) => dayjs(d).format("DD/MM")),
                  datasets: [
                    { label: 'Whatsapp', data: sortedMessageDays.map((d) => messagesPerDayByChannel[d]?.Whatsapp || 0), backgroundColor: '#34c759' },
                    { label: 'Web', data: sortedMessageDays.map((d) => messagesPerDayByChannel[d]?.Web || 0), backgroundColor: '#FF8F40' },
                    { label: 'Widget', data: sortedMessageDays.map((d) => messagesPerDayByChannel[d]?.Widget || 0), backgroundColor: '#afafaf' },
                    { label: 'Telegram', data: sortedMessageDays.map((d) => messagesPerDayByChannel[d]?.Telegram || 0), backgroundColor: '#5856d6' }
                  ]
                }}
                options={{
                  maintainAspectRatio: false,
                  scales: { x: { stacked: true }, y: { stacked: true } },
                  plugins: { legend: { display: false } }
                }}
              />
            </ChartWrapper>
          </ChartCard>

          <ChartCard span={2} height="300px">
            <InfoButton onClick={() => openModal('radar')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="chart-title">
              <span>Radar de Performance</span>
              <span style={{ fontSize: "10px", color: "#86868b", fontWeight: 500 }}>
                {intl.formatMessage({ id: "dashboard.radar.basedOnPeriod" })}
              </span>
            </div>
            <ChartWrapper>
              <Radar
                data={{
                  labels: [
                    intl.formatMessage({ id: "dashboard.radar.precision" }),
                    intl.formatMessage({ id: "dashboard.radar.velocity" }),
                    intl.formatMessage({ id: "dashboard.radar.rag" }),
                    intl.formatMessage({ id: "dashboard.radar.density" }),
                    intl.formatMessage({ id: "dashboard.radar.retention" }),
                  ],
                  datasets: [{
                    label: intl.formatMessage({ id: "dashboard.radar.dataset" }),
                    data: radarScores
                      ? [
                          radarScores.precision,
                          radarScores.velocity,
                          radarScores.rag,
                          radarScores.density,
                          radarScores.retention,
                        ]
                      : [0, 0, 0, 0, 0],
                    backgroundColor: "rgba(255, 143, 64, 0.22)",
                    borderColor: "#FF8F40",
                    borderWidth: 2,
                    pointBackgroundColor: "#FF8F40",
                    pointRadius: 4,
                    pointHoverRadius: 5,
                  }],
                }}
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    r: {
                      min: 0,
                      max: 100,
                      grid: { color: "#eee" },
                      angleLines: { color: "#f0f0f0" },
                      ticks: {
                        display: true,
                        stepSize: 25,
                        backdropColor: "transparent",
                        color: "#c1c1c1",
                        font: { size: 9 },
                        z: 1,
                      },
                      pointLabels: {
                        font: { size: 12, weight: "600" },
                        color: "#333",
                        padding: 10,
                      },
                    },
                  },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => `${ctx.label}: ${ctx.raw}/100`,
                      },
                    },
                  },
                }}
              />
            </ChartWrapper>
          </ChartCard>

          <ChartCard span={4} height="150px">
            <InfoButton onClick={() => openModal('summary')}><FontAwesomeIcon icon={faInfoCircle} /></InfoButton>
            <div className="chart-title">Resumo de Eficiência de Dados</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#86868b' }}>
                  {intl.formatMessage({ id: "dashboard.summary.dataMb" })}
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700' }}>{indexedMB}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#86868b' }}>
                  {intl.formatMessage({ id: "dashboard.summary.tokensPerFileKb" })}
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700' }}>{tokensPerFileKb}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#86868b' }}>
                  {intl.formatMessage({ id: "dashboard.summary.tokensPerKb" })}
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700' }}>{tokensPerDataKb}</div>
              </div>
            </div>
          </ChartCard>
        </BentoGrid>

        <MonthlySection>
          <SectionHeading>
            <h2>
              {intl.formatMessage({ id: "dashboard.monthlyTable.title" })}
            </h2>
            <p>{intl.formatMessage({ id: "dashboard.monthlyTable.sameScope" })}</p>
          </SectionHeading>

          {monthlyLoading ? (
            <TablePlaceholder>
              {intl.formatMessage({ id: "dashboard.monthlyTable.loading" })}
            </TablePlaceholder>
          ) : monthlyTableRows.every((r) => r.totalTokens === 0 && r.dataKb === 0) ? (
            <TablePlaceholder>
              {intl.formatMessage({ id: "dashboard.monthlyTable.empty" })}
            </TablePlaceholder>
          ) : (
            <TableContainer>
              <DataTable>
                <thead>
                  <tr>
                    <Th>{intl.formatMessage({ id: "dashboard.monthlyTable.col.month" })}</Th>
                    <Th className="numeric">
                      {intl.formatMessage({ id: "dashboard.monthlyTable.col.messageTokens" })}
                    </Th>
                    <Th className="numeric">
                      {intl.formatMessage({ id: "dashboard.monthlyTable.col.fileTokens" })}
                    </Th>
                    <Th className="numeric">
                      {intl.formatMessage({ id: "dashboard.monthlyTable.col.total" })}
                    </Th>
                    <Th className="numeric">
                      {intl.formatMessage({ id: "dashboard.monthlyTable.col.dataKb" })}
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {[...monthlyTableRows].reverse().map((row) => (
                    <tr key={row.key}>
                      <Td>{row.label}</Td>
                      <Td className="numeric">{formatNumber(row.messageTokens)}</Td>
                      <Td className="numeric">{formatNumber(row.fileTokens)}</Td>
                      <Td className="numeric total-col">{formatNumber(row.totalTokens)}</Td>
                      <Td className="numeric">{formatNumber(Math.round(row.dataKb))}</Td>
                    </tr>
                  ))}
                  <TotalRow>
                    <Td>{intl.formatMessage({ id: "dashboard.monthlyTable.rowTotal" })}</Td>
                    <Td className="numeric">{formatNumber(monthlyTotals.messageTokens)}</Td>
                    <Td className="numeric">{formatNumber(monthlyTotals.fileTokens)}</Td>
                    <Td className="numeric">{formatNumber(monthlyTotals.totalTokens)}</Td>
                    <Td className="numeric">{formatNumber(Math.round(monthlyTotals.dataKb))}</Td>
                  </TotalRow>
                </tbody>
              </DataTable>
            </TableContainer>
          )}
        </MonthlySection>

        <footer style={{ marginTop: '40px', textAlign: 'center', color: '#86868b', fontSize: '10px', letterSpacing: '1px', opacity: 0.6 }}>
          MUNDO 365 ENGINE • POWERED BY EVA AI • {dayjs().format('YYYY')}
        </footer>

        <Modal
          open={!!activeModal}
          onCancel={closeModal}
          footer={null}
          title={null}
          centered
        >
          {activeModal && cardDefinitions[activeModal] && (
            <ModalContent>
              <h3>{cardDefinitions[activeModal].title}</h3>
              {cardDefinitions[activeModal].content}
            </ModalContent>
          )}
        </Modal>
      </MainCard>
    </PageWrapper>
  );
};

export default Dashboard;